import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";
import { v2Fetch } from "@/lib/v2-api/client";
import type { UserResponse } from "@/app/api/types/user";

// Query keys
export const userKeys = {
  all: ["user"] as const,
  me: () => [...userKeys.all] as const,
} as const;

// Update user AI context
export function useUpdateUserAIContext() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ai_context: string | null) =>
      api.users.updateMyAIContext(ai_context),
    onMutate: async (newAIContext: string | null) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: userKeys.me() });

      // Snapshot previous value
      const previousUser = queryClient.getQueryData<UserResponse>(
        userKeys.me(),
      );

      // Optimistically update
      if (previousUser) {
        queryClient.setQueryData<UserResponse>(userKeys.me(), (old) => {
          if (!old) return old;
          return { ...old, ai_context: newAIContext };
        });
      }

      return { previousUser };
    },
    onError: (err, newAIContext, context) => {
      // Rollback on error
      if (context?.previousUser) {
        queryClient.setQueryData(userKeys.me(), context.previousUser);
      }
    },
    onSuccess: (updatedUser: UserResponse) => {
      // Update cache with server response
      queryClient.setQueryData(userKeys.me(), updatedUser);
    },
    onSettled: () => {
      // Refetch to ensure we're in sync
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

// Update user language preference (ISO 639-1, e.g. "fr", "en", "es").
//
// This is the SAME value the AI chat reads server-side ("Response language" in
// the agent context header). Persisting it here — instead of leaving the choice
// only in browser localStorage via i18n — is what makes the assistant adapt to
// each user's language across devices. Optimistically patches the ["user"]
// cache so the context header (auth-provider) and the i18n sync effect see the
// new value immediately.
export function useUpdateUserLanguage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (language: string) => api.users.putMe({ language }),
    onMutate: async (newLanguage: string) => {
      await queryClient.cancelQueries({ queryKey: userKeys.me() });
      const previousUser = queryClient.getQueryData<UserResponse>(
        userKeys.me(),
      );
      if (previousUser) {
        queryClient.setQueryData<UserResponse>(userKeys.me(), (old) =>
          old ? { ...old, language: newLanguage } : old,
        );
      }
      return { previousUser };
    },
    onError: (_err, _newLanguage, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData(userKeys.me(), context.previousUser);
      }
    },
    onSuccess: (updatedUser: UserResponse) => {
      queryClient.setQueryData(userKeys.me(), updatedUser);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

// Update user BI context — persistent, BI-mode-only instructions.
// Mirrors useUpdateUserAIContext; the value is stored on the user (under
// user_metadata) and injected into BI-mode chat prompts on start. The write
// goes through the V2 API (`PATCH /api/v2/bi-context/me`, self-scoped via the
// JWT) — the FastAPI endpoint it replaced has been removed. The read still
// comes from the user object (UserResponse.bi_context via GET /users/me).
export function useUpdateUserBIContext() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bi_context: string | null) =>
      v2Fetch<{ biContext: string | null }>("/api/v2/bi-context/me", {
        method: "PATCH",
        body: JSON.stringify({ biContext: bi_context }),
      }),
    onMutate: async (newBIContext: string | null) => {
      await queryClient.cancelQueries({ queryKey: userKeys.me() });
      const previousUser = queryClient.getQueryData<UserResponse>(
        userKeys.me(),
      );
      if (previousUser) {
        queryClient.setQueryData<UserResponse>(userKeys.me(), (old) =>
          old ? { ...old, bi_context: newBIContext } : old,
        );
      }
      return { previousUser };
    },
    onError: (_err, _newBIContext, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData(userKeys.me(), context.previousUser);
      }
    },
    onSuccess: (result: { biContext: string | null }) => {
      // Sync the cache with the value as the server stored it (blank → null).
      queryClient.setQueryData<UserResponse>(userKeys.me(), (old) =>
        old ? { ...old, bi_context: result.biContext } : old,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}
